import { ADMIN_PASSWORD_MIN_LENGTH } from '@voogzy/contracts';
import { type FormEvent, useEffect, useState } from 'react';
import { readError, request } from '../../app/api';
import { formatDateTime } from '../../shared/date-time';
import type { AdminAccessMode, AdminPermissionCode, AdminUser, ManagedAdminUser } from './types';

export function UsersView(props: { user: AdminUser; csrfToken: string }): React.JSX.Element {
  const [users, setUsers] = useState<ManagedAdminUser[]>([]);
  const [loading, setLoading] = useState(props.user.accessMode === 'full');
  const [error, setError] = useState('');

  useEffect(() => {
    if (props.user.accessMode === 'full') {
      void loadUsers();
    }
  }, [props.user.accessMode]);

  return (
    <section className="users-page">
      {error !== '' && <div className="error-banner">{error}</div>}
      <section className="users-top-grid">
        <AccountCard user={props.user} csrfToken={props.csrfToken} />
        {props.user.accessMode === 'full' ? <CreateUserCard csrfToken={props.csrfToken} onCreated={loadUsers} /> : <LimitedAccessCard />}
      </section>
      {props.user.accessMode === 'full' && (
        <article className="surface user-directory">
          <div className="surface-heading">
            <div><span className="eyebrow">Administration</span><h2>Platform users</h2></div>
            <strong>{loading ? 'Loading…' : `${users.length} accounts`}</strong>
          </div>
          <div className="user-list">
            {users.map(renderUser)}
          </div>
        </article>
      )}
    </section>
  );

  async function loadUsers(): Promise<void> {
    setLoading(true);
    setError('');
    try {
      const response = await request<{ users: ManagedAdminUser[] }>('/admin/v1/users');
      setUsers(response.users);
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setLoading(false);
    }
  }

  function renderUser(user: ManagedAdminUser): React.JSX.Element {
    return <ManagedUserRow key={user.id} user={user} currentUserId={props.user.id} csrfToken={props.csrfToken} onUpdated={loadUsers} />;
  }
}

function AccountCard(props: { user: AdminUser; csrfToken: string }): React.JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError('');
    setNotice('');
    if (newPassword !== confirmation) {
      setError('The new password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await request('/admin/v1/auth/change-password', {
        method: 'POST',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmation('');
      setNotice('Password changed. All other signed-in sessions were revoked.');
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="surface account-card">
      <div className="surface-heading"><div><span className="eyebrow">Your account</span><h2>{props.user.displayName}</h2></div><AccessBadge mode={props.user.accessMode} /></div>
      <p className="account-email">{props.user.email}</p>
      <h3>Change password</h3>
      <p className="muted-copy">Use at least {ADMIN_PASSWORD_MIN_LENGTH} characters. Changing it keeps this session open and signs out every other session.</p>
      <form className="settings-form" onSubmit={submit}>
        <label>Current password<input type="password" autoComplete="current-password" value={currentPassword} onChange={changeCurrentPassword} required /></label>
        <label>New password<input type="password" autoComplete="new-password" minLength={ADMIN_PASSWORD_MIN_LENGTH} value={newPassword} onChange={changeNewPassword} required /></label>
        <label>Confirm new password<input type="password" autoComplete="new-password" minLength={ADMIN_PASSWORD_MIN_LENGTH} value={confirmation} onChange={changeConfirmation} required /></label>
        {error !== '' && <div className="form-error">{error}</div>}
        {notice !== '' && <div className="success-banner">{notice}</div>}
        <button className="primary-button" disabled={busy}>{busy ? 'Changing…' : 'Change password'}</button>
      </form>
    </article>
  );

  function changeCurrentPassword(event: React.ChangeEvent<HTMLInputElement>): void { setCurrentPassword(event.target.value); }
  function changeNewPassword(event: React.ChangeEvent<HTMLInputElement>): void { setNewPassword(event.target.value); }
  function changeConfirmation(event: React.ChangeEvent<HTMLInputElement>): void { setConfirmation(event.target.value); }
}

function CreateUserCard(props: { csrfToken: string; onCreated: () => Promise<void> }): React.JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accessMode, setAccessMode] = useState<AdminAccessMode>('limited');
  const [manageSchema, setManageSchema] = useState(false);
  const [readSensitiveAnalytics, setReadSensitiveAnalytics] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    const permissions = accessMode === 'limited' ? buildPermissions(manageSchema, readSensitiveAnalytics) : [];
    try {
      await request('/admin/v1/users', {
        method: 'POST',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ displayName, email, password, accessMode, permissions }),
      });
      setDisplayName('');
      setEmail('');
      setPassword('');
      setAccessMode('limited');
      setManageSchema(false);
      setReadSensitiveAnalytics(false);
      setNotice('Account created. Share the initial password through a secure channel.');
      await props.onCreated();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="surface create-user-card">
      <div className="surface-heading"><div><span className="eyebrow">Full access only</span><h2>Add user</h2></div></div>
      <form className="settings-form" onSubmit={submit}>
        <label>Display name<input value={displayName} onChange={changeDisplayName} required /></label>
        <label>Email<input type="email" autoComplete="off" value={email} onChange={changeEmail} required /></label>
        <label>Initial password<input type="password" autoComplete="new-password" minLength={ADMIN_PASSWORD_MIN_LENGTH} value={password} onChange={changePassword} required /></label>
        <label>Access level<select value={accessMode} onChange={changeAccessMode}><option value="limited">Limited access</option><option value="full">Full access</option></select></label>
        {accessMode === 'limited' && <PermissionFields manageSchema={manageSchema} readSensitiveAnalytics={readSensitiveAnalytics} onManageSchemaChange={setManageSchema} onReadSensitiveAnalyticsChange={setReadSensitiveAnalytics} />}
        {error !== '' && <div className="form-error">{error}</div>}
        {notice !== '' && <div className="success-banner">{notice}</div>}
        <button className="primary-button" disabled={busy}>{busy ? 'Creating…' : 'Create user'}</button>
      </form>
    </article>
  );

  function changeDisplayName(event: React.ChangeEvent<HTMLInputElement>): void { setDisplayName(event.target.value); }
  function changeEmail(event: React.ChangeEvent<HTMLInputElement>): void { setEmail(event.target.value); }
  function changePassword(event: React.ChangeEvent<HTMLInputElement>): void { setPassword(event.target.value); }
  function changeAccessMode(event: React.ChangeEvent<HTMLSelectElement>): void { setAccessMode(event.target.value as AdminAccessMode); }
}

function ManagedUserRow(props: {
  user: ManagedAdminUser;
  currentUserId: string;
  csrfToken: string;
  onUpdated: () => Promise<void>;
}): React.JSX.Element {
  const [accessMode, setAccessMode] = useState<AdminAccessMode>(props.user.accessMode);
  const [manageSchema, setManageSchema] = useState(props.user.permissions.includes('analytics.schema.manage'));
  const [readSensitiveAnalytics, setReadSensitiveAnalytics] = useState(props.user.permissions.includes('analytics.sensitive.read'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isCurrent = props.user.id === props.currentUserId;
  const accessChanged = accessMode !== props.user.accessMode || (accessMode === 'limited' && (
    manageSchema !== props.user.permissions.includes('analytics.schema.manage')
    || readSensitiveAnalytics !== props.user.permissions.includes('analytics.sensitive.read')
  ));

  useEffect(() => {
    setAccessMode(props.user.accessMode);
    setManageSchema(props.user.permissions.includes('analytics.schema.manage'));
    setReadSensitiveAnalytics(props.user.permissions.includes('analytics.sensitive.read'));
  }, [props.user.accessMode, props.user.permissions]);

  async function saveAccess(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const permissions = accessMode === 'limited' ? buildPermissions(manageSchema, readSensitiveAnalytics) : [];
      await request(`/admin/v1/users/${encodeURIComponent(props.user.id)}/access`, {
        method: 'PUT',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ accessMode, permissions }),
      });
      await props.onUpdated();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled(): Promise<void> {
    const nextEnabled = !props.user.enabled;
    const action = nextEnabled ? 'enable' : 'disable';
    if (!window.confirm(`${capitalize(action)} ${props.user.displayName}'s account?`)) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      await request(`/admin/v1/users/${encodeURIComponent(props.user.id)}/status`, {
        method: 'PUT',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      await props.onUpdated();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={props.user.enabled ? 'managed-user-row' : 'managed-user-row disabled'}>
      <div className="managed-user-identity">
        <span className="user-avatar">{initials(props.user.displayName)}</span>
        <div><strong>{props.user.displayName}</strong><small>{props.user.email}</small><small>{lastSeenText(props.user.lastLoginAt)}</small></div>
      </div>
      <div className="managed-user-access">
        <label>Access<select value={accessMode} onChange={changeAccessMode} disabled={busy || isCurrent}><option value="limited">Limited</option><option value="full">Full</option></select></label>
        {accessMode === 'limited' && <PermissionFields manageSchema={manageSchema} readSensitiveAnalytics={readSensitiveAnalytics} onManageSchemaChange={setManageSchema} onReadSensitiveAnalyticsChange={setReadSensitiveAnalytics} disabled={busy || isCurrent} compact />}
        {accessMode === 'full' && <span className="full-access-copy">All current and future modules</span>}
      </div>
      <div className="managed-user-actions">
        <span className={props.user.enabled ? 'status-badge enabled' : 'status-badge'}>{props.user.enabled ? 'Enabled' : 'Disabled'}</span>
        {isCurrent ? <small>Current account</small> : <>
          <button className="secondary-button" disabled={busy || !accessChanged} onClick={saveAccess}>Save access</button>
          <button className="danger-button" disabled={busy} onClick={toggleEnabled}>{props.user.enabled ? 'Disable' : 'Enable'}</button>
        </>}
      </div>
      {error !== '' && <div className="form-error managed-user-error">{error}</div>}
    </section>
  );

  function changeAccessMode(event: React.ChangeEvent<HTMLSelectElement>): void { setAccessMode(event.target.value as AdminAccessMode); }
}

function PermissionFields(props: {
  manageSchema: boolean;
  readSensitiveAnalytics: boolean;
  onManageSchemaChange: (value: boolean) => void;
  onReadSensitiveAnalyticsChange: (value: boolean) => void;
  disabled?: boolean;
  compact?: boolean;
}): React.JSX.Element {
  function changeManageSchema(event: React.ChangeEvent<HTMLInputElement>): void {
    props.onManageSchemaChange(event.target.checked);
  }

  function changeReadSensitiveAnalytics(event: React.ChangeEvent<HTMLInputElement>): void {
    props.onReadSensitiveAnalyticsChange(event.target.checked);
  }

  return <div className={props.compact === true ? 'permission-fields compact' : 'permission-fields'}>
    <label><input type="checkbox" checked disabled /> View analytics</label>
    <label><input type="checkbox" checked={props.manageSchema} onChange={changeManageSchema} disabled={props.disabled} /> Edit and publish schemas</label>
    <label><input type="checkbox" checked={props.readSensitiveAnalytics} onChange={changeReadSensitiveAnalytics} disabled={props.disabled} /> View raw analytics IP addresses</label>
  </div>;
}

function LimitedAccessCard(): React.JSX.Element {
  return <article className="surface limited-access-card"><span className="eyebrow">Access</span><h2>Limited account</h2><p>Your permissions are managed by a full-access administrator. You can still update your own password here.</p></article>;
}

function AccessBadge(props: { mode: AdminAccessMode }): React.JSX.Element {
  return <span className={props.mode === 'full' ? 'access-badge full' : 'access-badge'}>{props.mode === 'full' ? 'Full access' : 'Limited access'}</span>;
}

function buildPermissions(manageSchema: boolean, readSensitiveAnalytics: boolean): AdminPermissionCode[] {
  const permissions: AdminPermissionCode[] = ['analytics.read'];
  if (manageSchema) {
    permissions.push('analytics.schema.manage');
  }
  if (readSensitiveAnalytics) {
    permissions.push('analytics.sensitive.read');
  }
  return permissions;
}

function initials(name: string): string {
  const parts = name.split(' ').slice(0, 2);
  let result = '';
  for (const part of parts) {
    result += part[0] ?? '';
  }
  return result.toUpperCase();
}

function lastSeenText(value: string | null): string {
  if (value === null) {
    return 'Never signed in';
  }
  return `Last sign-in ${formatDateTime(value)}`;
}

function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
